import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Link } from 'react-router-dom';
import { AiOutlineMinus, AiOutlinePlus, AiOutlineShopping } from 'react-icons/ai';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useStateContext } from '../context/StateContext';
import { client, urlFor } from '../sanity/sanityClient';
import ProductDisplay from './ProductDisplay';
import RecommendedProducts from './RecommendedProducts';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-lazy-load-image-component/src/effects/blur.css';
import './SlugProducts.css';

const PRODUCTS_PER_PAGE = 40;

// Hoisted so this literal isn't rebuilt every time the recommendations
// are (re)fetched.
const CATEGORIES = [
  "grocery", "beauty", "hotels", "estate", "clothingandfashion", "truck2cargo", "agritech", "cuisines",
  "kitchen", "crops", "electronics", "jewelryandaccessories", "energyandfuel",
  "salon", "foodandbeverages", "sports", "arts", "medical", "giftoptions", "booksandstationery",
  "vehicles", "livestock", "hardware", "useditems", "construction", "servicesandinstitutions",
  "kidsandtoys", "hospitality", "homeandfurnitures", "fashion", "eventsandnightclubs",
];

// One-item-per-category recommendation strip only needs a handful of
// fields, and a candidate pool of a few docs (not the whole category)
// to pick a random one from.
const RECOMMENDATION_PROJECTION = `{ _id, name, price, currency, slug, image }`;
const RECOMMENDATION_CANDIDATE_LIMIT = 5;

const getCurrency = () => localStorage.getItem('selectedCurrency') || 'TZS';

/**
 * Fetches one random product per category. Runs every category query
 * concurrently (Promise.all) instead of one-at-a-time, and each query
 * is capped to a small candidate pool with a narrow field projection
 * instead of pulling every product in the category just to discard
 * all but one.
 */
async function fetchOneRandomProductPerCategory() {
  const currency = getCurrency();

  const entries = await Promise.all(
    CATEGORIES.map(async (category) => {
      let candidates = await client.fetch(
        `*[_type == $category && currency == $currency][0...${RECOMMENDATION_CANDIDATE_LIMIT}]${RECOMMENDATION_PROJECTION}`,
        { category, currency }
      );

      if (candidates.length === 0) {
        candidates = await client.fetch(
          `*[_type == $category][0...${RECOMMENDATION_CANDIDATE_LIMIT}]${RECOMMENDATION_PROJECTION}`,
          { category }
        );
      }

      const product = candidates.length
        ? candidates[Math.floor(Math.random() * candidates.length)]
        : null;

      return [category, product];
    })
  );

  return Object.fromEntries(entries);
}

/**
 * Memoized desktop product card. Previously this was an inline render
 * function recreated (and re-run for every product) on any state
 * change in the parent — including bumping the quantity of a single,
 * unrelated product. As a real component wrapped in React.memo, a
 * card only re-renders when ITS OWN props actually change, as long as
 * the callback props stay referentially stable (see the useCallback
 * usage below).
 */
const DesktopProductCard = memo(function DesktopProductCard({
  product,
  quantity,
  onImageSelect,
  onAddToCart,
  onBuyNow,
  onQuantityChange,
  onLinkClick,
}) {
  return (
    <div className="pro-card">
      <div className="pro-image-wrap">
        <Link to={`/category/${product.slug.current}`} onClick={onLinkClick}>
          <LazyLoadImage
            src={urlFor(product.image[product.currentImageIndex])
              .width(500)
              .height(500)
              .auto('format')
              .quality(75)
              .url()}
            alt={product.name}
            effect="blur"
            className="pro-image"
          />
        </Link>
      </div>
      <div className="pro-thumbnails">
        {product.image.map((img, index) => (
          <LazyLoadImage
            key={index}
            src={urlFor(img).width(80).height(80).auto('format').quality(60).url()}
            className={`pro-thumb ${index === product.currentImageIndex ? 'active' : ''}`}
            onClick={() => onImageSelect(product._id, index)}
          />
        ))}
      </div>
      <div className="pro-content">
        <h3 className="pro-title">{product.name}</h3>

        <div className="pro-rating">
          <p>{product.location}</p>
          <p className="pro-price">
            {product.currency || 'TZS'} {product.price?.toLocaleString()}/=
          </p>
        </div>

        <p className="pro-details">{product.details}</p>

        <div className="pro-actions">
          <button className="pro-cart-add" onClick={() => onAddToCart(product)}>
            Add to <AiOutlineShopping />
          </button>
          <div className="qty-box">
            <AiOutlineMinus onClick={() => onQuantityChange(product._id, 'decrement')} />
            <span>{quantity}</span>
            <AiOutlinePlus onClick={() => onQuantityChange(product._id, 'increment')} />
          </div>

          <button className="pro-cart-buy" onClick={() => onBuyNow(product)}>
            Buy Now
          </button>
        </div>
      </div>
    </div>
  );
});

const SlugProducts = ({
  slugCategory,
  handleImageSelect,
  loadMoreProducts,
  hasMore,
  loading
}) => {
  const { onAdd, setShowCart } = useStateContext();
  const [isMobile, setIsMobile] = useState(
    window.matchMedia("(max-width: 768px)").matches
  );
  const [randomFinalProducts, setRandomFinalProducts] = useState({});
  const [displayedProducts, setDisplayedProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreDesktop, setHasMoreDesktop] = useState(true);

  const [productQuantities, setProductQuantities] = useState(() =>
    slugCategory.reduce((acc, product) => {
      acc[product._id] = 1;
      return acc;
    }, {})
  );

  // Kept in sync with productQuantities so handleAddToCart can read the
  // latest value without needing productQuantities as a dependency —
  // that would otherwise recreate the callback (and break card
  // memoization) every time any single product's quantity changes.
  const productQuantitiesRef = useRef(productQuantities);
  useEffect(() => {
    productQuantitiesRef.current = productQuantities;
  }, [productQuantities]);

  const sentinelRef = useRef(null);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleChange = (e) => setIsMobile(e.matches);
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  /* ------------------ Pagination Init ------------------ */
  useEffect(() => {
    if (slugCategory.length > 0) {
      const initialProducts = slugCategory.slice(0, PRODUCTS_PER_PAGE);
      setDisplayedProducts(initialProducts);
      setHasMoreDesktop(slugCategory.length > PRODUCTS_PER_PAGE);
      setCurrentPage(1);
    } else {
      setDisplayedProducts([]);
      setHasMoreDesktop(false);
    }
  }, [slugCategory]);

  /* ------------------ Load More Desktop ------------------ */
  const loadMoreDesktop = useCallback(() => {
    if (!hasMoreDesktop || loading) return;

    const nextPage = currentPage + 1;
    const startIndex = currentPage * PRODUCTS_PER_PAGE;
    const endIndex = startIndex + PRODUCTS_PER_PAGE;
    const nextProducts = slugCategory.slice(startIndex, endIndex);

    if (nextProducts.length > 0) {
      setDisplayedProducts(prev => [...prev, ...nextProducts]);
      setCurrentPage(nextPage);
      setHasMoreDesktop(endIndex < slugCategory.length);
    } else {
      setHasMoreDesktop(false);
    }
  }, [currentPage, hasMoreDesktop, slugCategory, loading]);

  /* ------------------ Infinite Scroll (IntersectionObserver) ------------------
     Replaces a raw `scroll` listener that recomputed
     document.body.offsetHeight on every scroll event — that forces a
     synchronous layout on the main thread dozens of times a second
     while scrolling. A sentinel element + IntersectionObserver only
     fires when it's actually about to enter the viewport, which is
     both cheaper and produces smoother scrolling. */
  useEffect(() => {
    if (isMobile) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreDesktop();
        }
      },
      { rootMargin: '600px 0px' } // start loading well before the user hits bottom
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile, loadMoreDesktop]);

  const handleQuantityChange = useCallback((productId, type) => {
    setProductQuantities((prevQuantities) => {
      const updatedQuantities = { ...prevQuantities };
      if (type === 'increment') {
        updatedQuantities[productId] = (updatedQuantities[productId] || 0) + 1;
      } else if (type === 'decrement' && updatedQuantities[productId] > 1) {
        updatedQuantities[productId] -= 1;
      }
      return updatedQuantities;
    });
  }, []);

  const handleBuyNow = useCallback((product) => {
    const productWithSelectedImage = {
      ...product,
      image: [product.image[product.currentImageIndex]],
      quantity: product.quantity || 1
    };

    onAdd(productWithSelectedImage, product.quantity || 1);
    setShowCart(true);
  }, [onAdd, setShowCart]);

  /* ------------------ Add To Cart ------------------ */
  const handleAddToCart = useCallback((product) => {
    const focusedImage = product.image[product.currentImageIndex];
    const productWithFocusedImage = { ...product, image: [focusedImage] };
    const qty = productQuantitiesRef.current[product._id] || 1;
    onAdd(productWithFocusedImage, qty);

    toast.success(`${qty} ${product.name} added to the cart.`, {
      position: toast.POSITION.TOP_CENTER,
      autoClose: 2500,
      hideProgressBar: true,
    });
  }, [onAdd]);

  const handleScrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const remainingProducts = displayedProducts.slice(4);

  /* ------------------ Fetch Random Per Category ------------------ */
  useEffect(() => {
    let cancelled = false;

    const getRandomProducts = async () => {
      const products = await fetchOneRandomProductPerCategory();
      if (!cancelled) setRandomFinalProducts(products);
    };

    getRandomProducts();

    window.addEventListener('storage', getRandomProducts);
    return () => {
      cancelled = true;
      window.removeEventListener('storage', getRandomProducts);
    };
  }, []);

  const mobileRecommendations = Object.entries(randomFinalProducts)
  .filter(([, product]) => product && product.slug?.current && product.image?.[0])
  .map(([category, product]) => ({ ...product, _type: product._type || category }));

  return (
    <>
      <ToastContainer />

      {/* Recommendations Section */}
      {isMobile ? (
        <RecommendedProducts
        products={mobileRecommendations}
        handleProductView={handleScrollToTop}
      />
      ) : (
        <div className="recommendations-wrapper">
          <div className="horizontal-scroll-container">
            {Object.entries(randomFinalProducts).map(([category, product]) => (
              <div key={category} className="product-card-elevated">
                {product ? (
                  <div className="product-card-content">
                    <Link
                      to={`/category/${product.slug.current}`}
                      onClick={handleScrollToTop}
                    >
                      {product.image[0] && (
                        <LazyLoadImage
                          src={urlFor(product.image[0])
                            .width(220)
                            .height(200)
                            .auto('format')
                            .url()}
                          alt={product.name}
                          effect="blur"
                        />
                      )}
                    </Link>
                    <p className="product-name-elevated">{product?.name || category}</p>
                    <p className="product-price-elevated">
                      {product.currency || 'TZS'}: {product.price?.toLocaleString()}/=
                    </p>
                  </div>
                ) : (
                  <p className="no-products">No products found in this category.</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {isMobile ? (
        <ProductDisplay
          products={slugCategory}
          handleImageSelect={handleImageSelect}
          handleAddToCart={handleAddToCart}
          loadMoreProducts={loadMoreProducts}
          hasMore={hasMore}
          loading={loading}
        />
      ) : (
        <div className="slug-wrapper">
          <div className="pro-grid featured-grid">
            {displayedProducts.slice(0, 4).map((product) => (
              <DesktopProductCard
                key={product._id}
                product={product}
                quantity={productQuantities[product._id] || 1}
                onImageSelect={handleImageSelect}
                onAddToCart={handleAddToCart}
                onBuyNow={handleBuyNow}
                onQuantityChange={handleQuantityChange}
                onLinkClick={handleScrollToTop}
              />
            ))}
          </div>

          <div className="pro-grid">
            {remainingProducts.map((product) => (
              <DesktopProductCard
                key={product._id}
                product={product}
                quantity={productQuantities[product._id] || 1}
                onImageSelect={handleImageSelect}
                onAddToCart={handleAddToCart}
                onBuyNow={handleBuyNow}
                onQuantityChange={handleQuantityChange}
                onLinkClick={handleScrollToTop}
              />
            ))}
          </div>

          {/* Sentinel for IntersectionObserver-driven infinite scroll */}
          <div ref={sentinelRef} style={{ height: 1 }} />

          {loading && <div className="end-message">Loading products...</div>}

          {!hasMoreDesktop && displayedProducts.length > 0 && (
            <div className="end-message">
              You've reached the end of products in this category
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default SlugProducts;
